import { flowRight, isEmpty, isNull } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { setRceTranslations } from '../../actions/set-rce-translations';

import { APP_COMPONENT_CLASS_NAME } from '../../constants/class-names';
import { setSearchParams } from '../../actions/set-search-params';
import { fetchCurrentMember } from '../../actions/fetch-current-member';
import { setAppLoaded } from '../../actions/app-loaded';
import { setDemoMode } from '../../actions/set-demo-mode';
import { setExperiments } from '../../actions/set-experiments';
import { setUser } from '../../actions/set-user';
import { setViewMode } from '../../actions/set-view-mode';
import { setUserAgent } from '../../actions/set-user-agent';
import fetchCategories from '../../actions/fetch-categories';
import { emitBlogOpened } from '../../actions/blog-socket';
import { fetchAppData } from '../../actions/fetch-app-data';
import { fetchAppSettings } from '../../actions/fetch-app-settings';
import { fetchCommonSettings } from '../../actions/fetch-common-settings';
import { overrideSettingsInOneApp } from '../../actions/override-settings-in-one-app';
import { enableNotificationCountChanges } from '../../actions/enable-notifications-count-change';
import { loadAndPersistWixModal } from '../../actions/load-and-persist-wix-modal';
import { setLocale } from '../../actions/set-locale';
import { contentMigrationFetchStatus } from '@wix/communities-blog-client-common/dist/src/actions/fetch-status';
import { getCurrentUser } from '@wix/communities-blog-client-common/dist/src/selectors/current-user-selectors';
import { getAppData } from '../../selectors/app-data-selectors';
import { getCommonSettings } from '../../selectors/common-settings-selectors';
import { getIsAppLoaded } from '../../selectors/is-app-loaded-selectors';
import { isEditor, isPreview } from '../../selectors/view-mode-selectors';
import AppComponent from '../../components/app';
import withTranslate from '../../hoc/with-translate';

export class App extends Component {
  static fetchData = ({
    store,
    user,
    demoMode,
    viewMode,
    experiments,
    userAgent,
    searchParams,
    locale,
    rceTranslations,
    fetchMigrationStatus,
  }) => {
    const promises = [
      store.dispatch(setSearchParams(searchParams)),
      store.dispatch(setViewMode(viewMode)),
      store.dispatch(setDemoMode(Boolean(demoMode))),
      store.dispatch(setExperiments(experiments)),
      store.dispatch(setUserAgent(userAgent)),
      store.dispatch(fetchCommonSettings()),
      store.dispatch(fetchCategories()),
      store.dispatch(fetchAppData()),
      store.dispatch(setLocale(locale)),
      store.dispatch(setRceTranslations(rceTranslations)),
    ];
    if (user) {
      promises.push(store.dispatch(setUser(user)));
    }
    if (fetchMigrationStatus && isEditor({ viewMode })) {
      promises.push(store.dispatch(contentMigrationFetchStatus(viewMode)));
    }

    return Promise.all(promises).then(() => store.dispatch(overrideSettingsInOneApp()));
  };

  componentDidMount() {
    const {
      isAppLoaded,
      appSettings,
      commonSettings,
      appData,
      categories,
      fetchAppSettings,
      fetchCommonSettings,
      fetchAppData,
      fetchCurrentMember,
      fetchCategories,
      emitBlogOpened,
      loadAndPersistWixModal,
      setAppLoaded,
      isEditor,
      isPreview,
    } = this.props;

    if (isAppLoaded) {
      return;
    }

    if (isEmpty(appSettings)) {
      fetchAppSettings();
    }
    if (isNull(commonSettings)) {
      fetchCommonSettings();
    }
    if (isEmpty(appData)) {
      fetchAppData();
    }
    if (!isEditor && !isPreview) {
      fetchCurrentMember();
    }
    if (!categories.length) {
      fetchCategories();
    }
    emitBlogOpened();

    loadAndPersistWixModal();

    setAppLoaded();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location.pathname === '/notifications') {
      this.props.enableNotificationCountChanges(false);
    } else {
      this.props.enableNotificationCountChanges(true);
    }
  }

  render() {
    return <AppComponent className={APP_COMPONENT_CLASS_NAME} {...this.props} />;
  }
}

App.propTypes = {
  fetchCurrentMember: PropTypes.func.isRequired,
  appSettings: PropTypes.object.isRequired,
  commonSettings: PropTypes.object.isRequired,
  appData: PropTypes.object.isRequired,
  fetchAppData: PropTypes.func.isRequired,
  fetchAppSettings: PropTypes.func.isRequired,
  fetchCommonSettings: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  fetchCategories: PropTypes.func,
  emitBlogOpened: PropTypes.func,
  enableNotificationCountChanges: PropTypes.func,
  loadAndPersistWixModal: PropTypes.func,
  setAppLoaded: PropTypes.func.isRequired,
  location: PropTypes.object,
  categories: PropTypes.array,
  isAppLoaded: PropTypes.bool.isRequired,
  isEditor: PropTypes.bool,
  isPreview: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => ({
  isAppLoaded: getIsAppLoaded(state),
  appSettings: state.appSettings,
  commonSettings: getCommonSettings(state),
  appData: getAppData(state),
  categories: state.categories,
  location: ownProps.location,
  currentUser: getCurrentUser(state),
  isEditor: isEditor(state),
  isPreview: isPreview(state),
});

const mapDispatchToProps = {
  emitBlogOpened,
  fetchAppData,
  fetchCurrentMember,
  fetchCategories,
  enableNotificationCountChanges,
  fetchAppSettings,
  fetchCommonSettings,
  loadAndPersistWixModal,
  setAppLoaded,
};

// prettier-ignore
export default flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslate
)(App);
